import { Typography, TypographyProps as MuiTypographyProps } from "@mui/material";
import { useMemo } from "react";
import { FoodpilotContextInitialValues, UserContext } from "src/context/FoodpilotContext";
import { StrategyInitialValues } from "src/context/Strategy/context";
import { PuckActionPlanContextValue } from "src/components/Puck/config/contexts/PuckActionPlanContext";
import { t } from "i18next";
import { useTokenResolver } from "src/components/Puck/config/fields/extensions/typingSuggestions/hooks/useTokenResolver";
import { DEFAULT_SCORE_PRECISION } from "src/context/Scores/provider";
import { type TextSpacing, textSpacingAttributes, textSpacingSx } from "src/components/Puck/config/layoutTokens";

export type TokenizedTextProps = {
    content: string;
    variant?: MuiTypographyProps["variant"];
    align?: "left" | "center" | "right" | "justify";
    color?: string;
    spacing?: TextSpacing;
};

export type TokenResolverContexts = {
    user: UserContext["trustedUser"] | {};
    company: FoodpilotContextInitialValues["selectedCompany"] | {};
    campaign: FoodpilotContextInitialValues["currentCampaign"] | {};
    actionPlan: PuckActionPlanContextValue | {};
    strategyData: StrategyInitialValues | {};
    scores: Record<string, unknown> | {};
};

const linkifyHtml = (html: string): string => {
    const parts = html.split(/(<a\s[^>]*>.*?<\/a>)/gi);
    return parts
        .map((part) => {
            if (/^<a\s/i.test(part)) return part;
            return part.replace(
                /(https?:\/\/[^\s<]+)/g,
                '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>',
            );
        })
        .join("");
};

export const TokenizedText = (props: TokenizedTextProps) => {
    const { content, variant = "body1", align = "left", color, spacing } = props;

    const { processToken } = useTokenResolver(DEFAULT_SCORE_PRECISION);

    const processedContent = useMemo(() => {
        return processToken(content);
    }, [content, processToken]);

    const isTranslationKey = useMemo(() => {
        return (
            typeof processedContent === "string" &&
            processedContent.trim() !== "" &&
            !/\s/u.test(processedContent) &&
            /^[\p{L}\p{N}._-]+$/u.test(processedContent)
        );
    }, [processedContent]);

    const displayContent = useMemo(() => {
        const resolved = isTranslationKey ? t(processedContent, processedContent) : processedContent;
        return linkifyHtml(resolved);
    }, [isTranslationKey, processedContent]);

    return (
        <Typography
            dangerouslySetInnerHTML={{ __html: displayContent }}
            variant={variant}
            align={align}
            color={color}
            sx={{
                whiteSpace: "break-spaces",
                overflowWrap: "anywhere",
                ...textSpacingSx(spacing),
                "& a": {
                    color: "primary.main",
                    textDecoration: "none",
                    cursor: "pointer",
                    "&:hover": {
                        textDecoration: "underline",
                    },
                },
            }}
            onClick={(e) => {
                const target = e.target as HTMLElement;
                if (target.tagName === "A" || target.closest("a")) {
                    return;
                }
                e.stopPropagation();
            }}
            {...textSpacingAttributes(spacing)}
        />
    );
};

TokenizedText.displayName = "TokenizedText";
